import { inject } from '@angular/core';
import { CanActivateFn, Params, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  OTHER_COLLECTION_ID,
  QueryApiService,
} from 'src/app/shared/services/customer-api/query-api.service';

/** The filters a collection redirect keeps from the requested URL. */
const carriedParams = (params: Params) => ({
  news: params.news,
  highlight: params.highlight,
  time: params.time,
  filter: params.filter,
  lang: params.lang ? params.lang : 'en,de',
  feed: params.feed
    ? params.feed
    : 'non_professional,online,print,professional',
  ranks: params.ranks,
  panels: params.panels,
  categories: params.categories ? params.categories : '',
  continents: params.continents,
  countries: params.countries,
  factuality: params.factuality,
  temporality: params.temporality,
  event_ids: params.event_ids,
  sort: 'ratio',
  nomenu: params.nomenu,
  notitle: params.notitle,
});

/**
 * Resolves the collection (or single query) in the URL into QueryApiService.
 * Without a collection id it opens the alphabetically-first collection; a user
 * with no collections lands on the collection page's empty state.
 * `id=other` is the pseudo-collection of queries that belong to no collection;
 * `groupId=default|other` marks a single query opened outside any collection.
 */
export const queryGuard: CanActivateFn = (_route, state) => {
  const queryApi = inject(QueryApiService);
  const router = inject(Router);
  const params = state.root.queryParams;
  const path = state.url.split('?')[0];

  const land = (url: string, collectionId?: string) => {
    router.navigate([url], {
      queryParams: {
        ...(collectionId ? { id: collectionId, groupId: collectionId } : {}),
        ...carriedParams(params),
      },
    });
    return false;
  };

  const firstCollection = (groups) =>
    [...groups].sort((a, b) => a.name.localeCompare(b.name))[0].uid;

  return forkJoin({
    groups: queryApi.getGroupsQuery(),
    queries: queryApi.getQuery(),
  }).pipe(
    map((result) => {
      queryApi.querys = result.groups;
      queryApi.iquery = undefined;
      queryApi.query = undefined;
      const collectionIds = new Set(result.groups.map((g) => g.uid));
      const orphans = result.queries.filter(
        (q) => !(q.groups ?? []).some((uid) => collectionIds.has(uid)),
      );
      queryApi.otherQueries = orphans;

      const isSingleQuery =
        ['default', OTHER_COLLECTION_ID].includes(params.groupId) &&
        params.id &&
        params.id !== params.groupId &&
        !collectionIds.has(params.id);
      const singleQuery =
        isSingleQuery && result.queries.find((q) => q.uid === params.id);
      if (singleQuery) {
        queryApi.query = singleQuery;
        return true;
      }

      if (params.id === OTHER_COLLECTION_ID && orphans.length) {
        // Other has no collection-level chart.
        if (path.endsWith('/chart')) {
          return land(path.replace(/\/chart$/, '/main'), OTHER_COLLECTION_ID);
        }
        queryApi.iquery = orphans;
        queryApi.query = {
          uid: OTHER_COLLECTION_ID,
          name: 'Other',
          query: {
            operator: 'or',
            filters: [],
            groups: orphans.map((q) => q.query),
          },
        };
        return true;
      }

      if (!result.groups[0]) {
        if (!params.id && path.endsWith('/main')) {
          return true;
        }
        return land(path.replace(/\/chart$/, '/main'));
      }

      if (
        !params.id ||
        params.id === 'default' ||
        params.id === OTHER_COLLECTION_ID
      ) {
        return land(path, firstCollection(result.groups));
      }

      let collectionIdExists = false;
      let queryIdExists = false;
      result.groups.map((group) => {
        if (group.uid === params.id) {
          queryApi.iquery = group.content;
          queryApi.query = {
            uid: group.uid,
            name: group.name,
            query: {
              operator: 'or',
              filters: [],
              groups: group.content.map((query) => query.query),
            },
          };
        } else {
          group.content.map((query) => {
            if (query.uid === params.id) {
              queryApi.query = query;
            }
          });
        }
        if (group.uid === params.groupId) {
          collectionIdExists = true;
        }
      });
      if (queryApi.query === undefined) {
        result.queries.map((query) => {
          if (query.uid === params.id) {
            queryApi.query = query;
            queryIdExists = true;
          }
        });
      } else {
        queryIdExists = true;
      }
      if (!queryIdExists || !collectionIdExists) {
        return land(path, firstCollection(result.groups));
      }
      return true;
    }),
  );
};
